/**
 * Brand-agnostic funnel orchestrator.
 *
 * Drives `<SurveyStep>` from `@nextday/survey-kit` against a `FunnelConfig`
 * resolved at runtime. Replaces the demo-only orchestrator that hardcoded
 * a `STEP_RENDERERS` lookup against a per-key React tree (see plan §11
 * item 3 — "Single brand-agnostic Funnel.tsx").
 *
 * Config source: `apps/funnel-api`'s `/api/funnel/resolve` (D1-backed,
 * variant-aware). The bundled SecretMate seed JSON used during the spike
 * (NX-9281) is no longer reachable from the prod bundle as of NX-9371 —
 * Vite tree-shakes it because `Funnel.tsx` is the only previous consumer.
 *
 * Three visible states:
 *   1. `loading`  — application or funnel-config fetch in flight.
 *   2. `error`    — fatal funnel-load failure (per PLAN.md §6.1, never silently fall back).
 *   3. `ready`    — engine running.
 */
import {
  INVISIBLE_AUTO_ADVANCING_STEP_TYPES,
  getProgress,
  type FunnelConfig,
  type FunnelState,
  type Step,
} from "@nextday/funnel-engine";
import { ContinueButton, FunnelHeader, FunnelShell, StepTransition } from "@nextday/funnel-ui";
import type { StepTransitionDirection, ProgressIndicatorStyle } from "@nextday/funnel-ui";
import { SurveyStep, isWideLayout } from "@nextday/survey-kit";
import type { AnswerInput, SessionContext } from "@nextday/survey-kit";
import { memo, useCallback, useEffect, useMemo, useState, type ReactElement } from "react";

import {
  initializeAttribution,
  readAttributionContext,
  stampFunnelId,
  stampSessionId,
} from "@nextday/attribution";
import {
  attributionChangedSinceDraft,
  seedAttributionAnswers,
} from "@src/funnel/attributionAnswers";
import { flattenAttribution, type FlatAttribution } from "@src/funnel/flattenAttribution";
import { useFunnelLayout } from "@src/funnel/FunnelLayoutContext";
import { useAuthUserId } from "@src/auth/useAuthUserId";
import { useFunnelLoginHandler } from "@src/auth/useFunnelLoginHandler";
import DevHandoffShortcut from "@src/funnel/DevHandoffShortcut";
import { buildFunnelRegistry } from "@src/funnel/buildFunnelRegistry";
import { FunnelErrorShell, FunnelLoadingShell } from "@src/funnel/FunnelStatusShell";
import {
  useFunnelAnalytics,
  type FunnelAnalyticsContext,
} from "@src/funnel/hooks/useFunnelAnalytics";
import { useFunnelAnalyticsLifecycle } from "@src/funnel/hooks/useFunnelAnalyticsLifecycle";
import { useFunnelState } from "@src/funnel/hooks/useFunnelState";
import { useSessionInit } from "@src/funnel/hooks/useSessionInit";
import { useAnonymousId } from "@src/funnel/useAnonymousId";
import { useApplicationContext, type ResolvedApplication } from "@src/funnel/useApplicationContext";
import { useFunnelDraft, type FunnelDraftAnswers } from "@src/funnel/useFunnelDraft";
import { clearPersistedSession } from "@src/funnel/sessionPersistence";
import { useTapfiliateClick } from "@src/funnel/useTapfiliateClick";
import { useFunnelHandlers } from "@src/funnel/useFunnelHandlers";
import { useHandoffRedirect } from "@src/funnel/useHandoffRedirect";
import { usePixelTracking } from "@src/tracking/usePixelTracking";

/**
 * Well-known answer-bag key for the per-funnel handoff destination URL.
 * Mirrors `HANDOFF_URL_ANSWER_KEY` in `handoff_config.tsx` — duplicated here
 * to avoid pulling survey-kit renderer sources into the funnel's rootDir.
 */
const HANDOFF_URL_ANSWER_KEY = "__handoff_url";

// Step types whose renderer owns no CTA and therefore need the orchestrator's
// global `ContinueButton` footer to advance.
//
// Input renderers (multi_choice, slider, name_capture) collect values and fire
// `onAnswer` but have no built-in submit button — advancing is the user's
// explicit commit action, which is the global footer's job.
//
// Non-terminal display renderers (info_screen, trust_screen, validation_screen)
// have pure-display bodies; their JSDoc explicitly delegates to this footer.
// `validation_screen` self-advances when `autoAdvanceMs > 0`; the footer is
// the fallback when `autoAdvanceMs === 0` (disabled in seed).
//
// Terminal renderers (paywall_handoff, paywall, handoff) use the footer to
// trigger the handoff redirect rather than `onAdvance`.
const STEP_TYPES_WITH_GLOBAL_FOOTER: ReadonlySet<string> = new Set([
  "multi_choice",
  "slider",
  "name_capture",
  "info_screen",
  "trust_screen",
  "validation_screen",
  "paywall_handoff",
  "paywall",
  "handoff",
]);

const TERMINAL_STEP_TYPES: ReadonlySet<string> = new Set(["paywall_handoff", "paywall", "handoff"]);

const VALID_PROGRESS_STYLES: ReadonlyArray<ProgressIndicatorStyle> = [
  "numbered",
  "dots",
  "gradient_bar",
  "hybrid",
  "pill",
];

const resolveProgressStyle = (
  stepLevel: unknown,
  brandLevel: ProgressIndicatorStyle | undefined,
): ProgressIndicatorStyle => {
  if (
    typeof stepLevel === "string" &&
    (VALID_PROGRESS_STYLES as ReadonlyArray<string>).includes(stepLevel)
  ) {
    return stepLevel as ProgressIndicatorStyle;
  }
  return brandLevel ?? "gradient_bar";
};

type FunnelEngineRunnerProps = {
  application: ResolvedApplication;
  config: FunnelConfig;
  /** Per-funnel display overrides (NX-9597). */
  displayConfig: Record<string, unknown>;
  sessionId: string;
  anonymousId: string;
  experimentId?: string;
  /** NX-9949: Experiment arm id assigned via bucketing. */
  armId?: string;
  /** NX-9847: Published funnel version number. */
  funnelVersion?: number;
  /** Resolved inlet D1 row id — merged into attribution on analytics events. */
  inletId?: string;
  /** NX-10213: True when the funnel was resolved in preview/draft mode. */
  isPreview?: boolean;
};

type FunnelEngineViewProps = {
  brandIdentity: {
    id: string;
    logoAlt: string;
    logoUrl?: string;
    progressStyle?: ProgressIndicatorStyle;
  };
  brandDisplayName: string;
  showBack: boolean;
  showLogin: boolean;
  onBack: () => void;
  onLoginPress: (() => void) | null;
  progress: { index: number; total: number };
  renderGlobalFooter: boolean;
  isResolvingBranch: boolean;
  isStepInvalid: boolean;
  handoffRedirect: () => void;
  onAdvance: () => void;
  currentStep: Step | null;
  /** Step rendered behind the drawer when `presentation: "drawer"`. */
  previousStep: Step | null;
  direction: StepTransitionDirection;
  registry: ReturnType<typeof buildFunnelRegistry>;
  answers: FunnelDraftAnswers;
  session: SessionContext;
  engineState: FunnelState;
  onAnswer: (answerKey: string, input: AnswerInput) => void;
  onSkip: () => void;
  onValidityChange: (valid: boolean) => void;
  onRestart: () => void;
};

const NOOP = (): void => {};

const FunnelEngineView = memo(
  ({
    brandIdentity,
    brandDisplayName,
    showBack,
    showLogin,
    onBack,
    onLoginPress,
    progress,
    renderGlobalFooter,
    isResolvingBranch,
    isStepInvalid,
    handoffRedirect,
    onAdvance,
    currentStep,
    previousStep,
    direction,
    registry,
    answers,
    session,
    engineState,
    onAnswer,
    onSkip,
    onValidityChange,
    onRestart,
  }: FunnelEngineViewProps): ReactElement => {
    const isDrawer = currentStep?.data?.presentation === "drawer";

    return (
      <FunnelShell
        brand={brandIdentity}
        header={
          isDrawer ? undefined : (
            <FunnelHeader
              brandName={brandDisplayName}
              logoUrl={brandIdentity.logoUrl}
              showBack={showBack}
              onBack={onBack}
              showLogin={showLogin}
              onLogin={onLoginPress ?? undefined}
              progress={
                currentStep?.showProgress === false
                  ? undefined
                  : {
                      style: resolveProgressStyle(
                        currentStep?.data?.progressStyle,
                        brandIdentity.progressStyle,
                      ),
                      current: progress.index,
                      total: progress.total,
                    }
              }
            />
          )
        }
        footer={
          renderGlobalFooter && !isDrawer ? (
            <>
              <ContinueButton
                label={
                  typeof currentStep?.data?.ctaLabel === "string"
                    ? currentStep.data.ctaLabel
                    : "Continue"
                }
                onPress={
                  currentStep !== null && TERMINAL_STEP_TYPES.has(currentStep.type)
                    ? handoffRedirect
                    : onAdvance
                }
                disabled={isResolvingBranch || isStepInvalid}
              />
              {typeof currentStep?.data?.footnote === "string" && (
                <p
                  className="m-0 pb-1 pt-2 text-center text-xs"
                  style={{ color: "hsl(var(--hero-fg-muted))" }}
                >
                  {String(currentStep.data.footnote)}
                </p>
              )}
            </>
          ) : null
        }
      >
        {/* eslint-disable-next-line sayari/no-unwrapped-jsx-text -- ternary yields elements, not text */}
        {currentStep && isDrawer ? (
          <>
            {previousStep && (
              <div className="pointer-events-none" aria-hidden="true">
                <SurveyStep
                  step={previousStep}
                  registry={registry}
                  answers={answers}
                  session={session}
                  funnelState={engineState}
                  onAnswer={NOOP}
                  onAdvance={NOOP}
                />
              </div>
            )}
            <SurveyStep
              step={currentStep}
              registry={registry}
              answers={answers}
              session={session}
              funnelState={engineState}
              onAnswer={onAnswer}
              onAdvance={onAdvance}
              onSkip={onSkip}
              onValidityChange={onValidityChange}
              onRestart={onRestart}
            />
          </>
        ) : currentStep ? (
          <StepTransition stepKey={currentStep.id} direction={direction}>
            <SurveyStep
              step={currentStep}
              registry={registry}
              answers={answers}
              session={session}
              funnelState={engineState}
              onAnswer={onAnswer}
              onAdvance={onAdvance}
              onSkip={onSkip}
              onValidityChange={onValidityChange}
              onRestart={onRestart}
            />
          </StepTransition>
        ) : null}
        <DevHandoffShortcut onTrigger={handoffRedirect} />
      </FunnelShell>
    );
  },
);

/**
 * Inner engine driver. Mounted only once application + funnel config have
 * resolved successfully — receives them as props so its hooks (which depend
 * on `funnel_id` / `override_id`) can be called unconditionally.
 */
const FunnelEngineRunner = ({
  application,
  config,
  displayConfig,
  sessionId,
  anonymousId,
  experimentId,
  armId,
  funnelVersion,
  inletId,
  isPreview,
}: FunnelEngineRunnerProps): ReactElement => {
  const registry = useMemo(buildFunnelRegistry, []);

  const { initialDraft, saveDraft, clearDraft } = useFunnelDraft({
    funnelId: config.id,
    overrideId: config.overrideId,
    steps: config.steps,
  });

  const [rawAttribution, setRawAttribution] = useState<FlatAttribution | undefined>(() =>
    flattenAttribution(readAttributionContext()),
  );

  const [answers, setAnswers] = useState<FunnelDraftAnswers>(() => initialDraft?.answers ?? {});
  const initialState = initialDraft?.engineState;
  const {
    state: engineState,
    currentStep,
    advance,
    goBack,
    restart,
  } = useFunnelState(config, initialState !== undefined ? { initialState } : {});

  // Tracks whether the attribution boot + sessionId/funnel_id stamps have finished.
  // The handoff redirect must wait for this before building the `ad_h` blob,
  // otherwise `buildOutboundHandoffUrl` reads the context before the stamps
  // have written `lastTouch.sessionId` / `lastTouch.funnelId` and the handoff
  // loses them. Steps are also not mounted until then, so `value_map` /
  // templates reading `__utm_content` etc. see the attribution of *this* click.
  const [attributionReady, setAttributionReady] = useState(false);

  useEffect(() => {
    setAttributionReady(false);
    const afterBoot = (): void => {
      stampSessionId(sessionId);
      stampFunnelId(config.id);
      const flat = flattenAttribution(readAttributionContext());
      setRawAttribution(flat);
      const seedSource = inletId ? { ...flat, inlet_id: inletId } : flat;
      // A resumed draft whose inbound ad changed must re-run the mapping
      // steps, otherwise derived answers (character, handoff URL) stay stale.
      if (
        initialDraft !== null &&
        attributionChangedSinceDraft(initialDraft.answers, seedSource, initialDraft.savedAt)
      ) {
        setAnswers(seedAttributionAnswers({}, seedSource));
        restart();
      } else {
        setAnswers((prev) => seedAttributionAnswers(prev, seedSource));
      }
      setAttributionReady(true);
    };
    void initializeAttribution(undefined).then(afterBoot, afterBoot);
  }, [sessionId, config.id, inletId, initialDraft, restart]);

  const authUserId = useAuthUserId();
  const attribution = useMemo(
    () =>
      inletId && rawAttribution
        ? { ...rawAttribution, inlet_id: inletId }
        : inletId
          ? { inlet_id: inletId }
          : rawAttribution,
    [rawAttribution, inletId],
  );

  const analyticsContext = useMemo<FunnelAnalyticsContext>(
    () => ({
      applicationId: application.id,
      funnelId: config.id,
      overrideId: config.overrideId,
      sessionId,
      anonymousId,
      ...(authUserId ? { userId: authUserId } : {}),
      ...(experimentId ? { experimentId } : {}),
      ...(armId ? { armId } : {}),
      ...(funnelVersion !== undefined ? { funnelVersion } : {}),
      ...(attribution ? { attribution } : {}),
    }),
    [
      application.id,
      config.id,
      config.overrideId,
      sessionId,
      anonymousId,
      authUserId,
      experimentId,
      armId,
      funnelVersion,
      attribution,
    ],
  );
  const firePixels = usePixelTracking({
    sessionId,
    anonymousId,
    funnelId: config.id,
    overrideId: config.overrideId,
    applicationId: application.id,
    ga4MeasurementId: application.ga4MeasurementId,
    analyticsEnabled: application.analyticsEnabled,
    isPreview: Boolean(isPreview),
  });
  // NX-10230: Single-step funnels (e.g. redirect-only) auto-complete and
  // navigate away immediately. The queue/timer/flush lifecycle cannot
  // reliably fire before the page unloads, so we bypass the queue and send
  // each event directly via sendBeacon.
  const isSingleStep = config.steps.length <= 1;
  const analytics = useFunnelAnalytics(analyticsContext, {
    onPixelFire: firePixels,
    directMode: isSingleStep,
  });

  const funnelContext = useMemo(
    () => ({
      application_id: application.slug,
      character_id: undefined,
    }),
    [application.slug],
  );

  const { direction, isResolvingBranch, onAnswer, onAdvance, onBack } = useFunnelHandlers({
    currentStep,
    answers,
    setAnswers,
    advance,
    goBack,
    analytics,
    context: funnelContext,
    config,
  });

  // Persist on every state change. The hook debounces internally so this
  // is safe to call as often as React re-renders.
  useEffect(() => {
    saveDraft(engineState, answers);
  }, [engineState, answers, saveDraft]);

  const onBeforeRedirect = useCallback(() => {
    clearDraft();
    clearPersistedSession(config.id, config.overrideId);
  }, [clearDraft, config.id, config.overrideId]);

  const handoffContext = useMemo(
    () => ({ status: "ready" as const, application, inlet: null, funnel: null }),
    [application],
  );
  const rawHandoffRedirect = useHandoffRedirect(
    handoffContext,
    onBeforeRedirect,
    answers,
    config.steps,
  );
  const handoffRedirect = useCallback(() => {
    analytics.emit("funnel_handoff_clicked");
    rawHandoffRedirect();
  }, [analytics, rawHandoffRedirect]);
  const onLoginPress = useFunnelLoginHandler();

  const session: SessionContext = useMemo(
    () => ({
      session_id: sessionId,
      anonymous_id: anonymousId,
      application_id: application.slug,
    }),
    [anonymousId, application.slug, sessionId],
  );

  // Delegate to the engine's `getProgress` rather than reimplementing here.
  // The engine walks `history` backward so interstitial steps
  // (`advances_progress: false`) hold at the previous counted position
  // instead of snapping the bar to 0 (Bugbot NX-9280). Indices are 1-based;
  // see the `showLogin` threshold below.
  const progress = useMemo(() => getProgress(engineState, config), [engineState, config]);
  const isResumedSession = initialDraft !== null;
  useFunnelAnalyticsLifecycle({
    analytics,
    currentStep,
    stepIndex: progress.index,
    isResumedSession,
  });

  // NX-10329: When the current terminal step has a `handoffUrl` in its data
  // (e.g. paywall_handoff), write it to the well-known `__handoff_url`
  // answer-bag key so `useHandoffRedirect` can override `redirect_url` on the
  // Clerk token-signin URL. Without this, the user lands on the generic
  // post-auth page instead of the configured destination (e.g. character page).
  useEffect(() => {
    if (
      currentStep &&
      TERMINAL_STEP_TYPES.has(currentStep.type) &&
      typeof currentStep.data?.handoffUrl === "string" &&
      currentStep.data.handoffUrl !== ""
    ) {
      setAnswers((prev) => {
        // Don't overwrite if already set (e.g. by a preceding handoff_config step).
        if (prev[HANDOFF_URL_ANSWER_KEY] != null && prev[HANDOFF_URL_ANSWER_KEY] !== "")
          return prev;
        return { ...prev, [HANDOFF_URL_ANSWER_KEY]: currentStep.data.handoffUrl };
      });
    }
  }, [currentStep, setAnswers]);

  // NX-10230: Auto-trigger handoff once the engine reaches the terminal step.
  // MUST be declared AFTER useFunnelAnalyticsLifecycle so that React runs
  // the analytics effects (funnel_landing_viewed, funnel_step_viewed) first.
  // Previously this effect ran before the lifecycle hook, causing events to
  // be lost when flushEventQueueSync fired before they were enqueued —
  // particularly for single-step redirect funnels (the Clerk sync path).
  //
  // NX-10537: Also gated on `attributionReady` so that `buildOutboundHandoffUrl`
  // reads the attribution context AFTER `stampSessionId` has written
  // `lastTouch.sessionId`. Without this, one-step redirect funnels fire the
  // handoff in the same render cycle as the stamp useEffect, but the stamp's
  // `.then()` callback hasn't run yet — so the `ad_h` blob is serialised
  // without `sessionId` and downstream CAPI events are rejected by WAE.
  useEffect(() => {
    if (engineState.completed && attributionReady) handoffRedirect();
  }, [engineState.completed, attributionReady, handoffRedirect]);

  // NX-9597: Cascade display config — funnel-level overrides app-level.
  const brandIdentity = useMemo(() => {
    const resolveStr = (key: string): string | undefined => {
      const fv = displayConfig[key];
      if (typeof fv === "string") return fv;
      const av = application.themeTokens[key];
      if (typeof av === "string") return av;
      return undefined;
    };
    const ps = resolveStr("progress_style");
    return {
      id: application.slug,
      logoAlt: application.name,
      logoUrl: resolveStr("logo_url"),
      progressStyle:
        ps !== undefined && (VALID_PROGRESS_STYLES as ReadonlyArray<string>).includes(ps)
          ? (ps as ProgressIndicatorStyle)
          : undefined,
    };
  }, [application.slug, application.name, application.themeTokens, displayConfig]);

  const previousStep = useMemo((): Step | null => {
    const { history } = engineState;
    for (let i = history.length - 2; i >= 0; i -= 1) {
      const step = config.steps.find((s) => s.id === history[i]);
      if (step && !INVISIBLE_AUTO_ADVANCING_STEP_TYPES.has(step.type)) return step;
    }
    return null;
  }, [engineState.history, config.steps]);

  // Nothing interactive (step, footer Continue, Back) renders until the
  // attribution boot has seeded the answer bag for this click.
  const visibleStep = attributionReady ? currentStep : null;
  const showBack = attributionReady && previousStep !== null;

  const { setWideLayout } = useFunnelLayout();
  const [wideStepType, setWideStepType] = useState<string | undefined>();
  if (visibleStep?.type !== wideStepType) {
    setWideStepType(visibleStep?.type);
    setWideLayout(visibleStep !== null && isWideLayout(registry, visibleStep.type));
  }
  useEffect(() => () => setWideLayout(false), [setWideLayout]);

  const renderGlobalFooter =
    visibleStep !== null && STEP_TYPES_WITH_GLOBAL_FOOTER.has(visibleStep.type);

  // --- Validity gating (NX-9586) ---
  // Renderers report validity via onValidityChange; default to valid so
  // steps without validation (info_screen, trust_screen…) never block.
  // Reset synchronously during render (not in useEffect) so the child
  // renderer's mount effect can override it — React fires child effects
  // before parent effects, so a useEffect reset would overwrite the
  // child's onValidityChange(false) report.
  const [isStepValid, setIsStepValid] = useState(true);
  const [validityStepId, setValidityStepId] = useState(currentStep?.id);
  if (currentStep?.id !== validityStepId) {
    setValidityStepId(currentStep?.id);
    setIsStepValid(true);
  }

  const onValidityChange = useCallback((valid: boolean) => {
    setIsStepValid(valid);
  }, []);

  // --- Restart handler (NX-9587) ---
  // Don't call clearDraft() here — it permanently disables saveDraft via
  // clearedRef. Instead, just reset answers + engine; the saveDraft effect
  // will overwrite the old draft with the reset state on next render.
  const onRestart = useCallback(() => {
    setAnswers(seedAttributionAnswers({}, attribution));
    restart();
  }, [restart, setAnswers, attribution]);

  return (
    <FunnelEngineView
      brandIdentity={brandIdentity}
      brandDisplayName={application.name}
      showBack={showBack}
      showLogin={
        // NX-9597 cascade: step > funnel > default (true).
        // displayConfig keys use snake_case to match theme_tokens convention.
        (currentStep?.showLogin ?? displayConfig.show_login) !== false &&
        progress.index <= 3 &&
        onLoginPress !== null
      }
      onBack={onBack}
      onLoginPress={onLoginPress}
      progress={progress}
      renderGlobalFooter={renderGlobalFooter}
      isResolvingBranch={isResolvingBranch}
      isStepInvalid={!isStepValid}
      handoffRedirect={handoffRedirect}
      onAdvance={onAdvance}
      currentStep={visibleStep}
      previousStep={previousStep}
      direction={direction}
      registry={registry}
      answers={answers}
      session={session}
      engineState={engineState}
      onAnswer={onAnswer}
      onSkip={onAdvance}
      onValidityChange={onValidityChange}
      onRestart={onRestart}
    />
  );
};

type FunnelWithConfigProps = {
  application: ResolvedApplication;
  config: FunnelConfig;
  displayConfig: Record<string, unknown>;
  experimentId?: string;
  /** NX-9949: Experiment arm id assigned via bucketing. */
  armId?: string;
  /** NX-9847: Published funnel version number. */
  funnelVersion?: number;
  /** Resolved inlet D1 row id. */
  inletId?: string;
  /** NX-10213: True when the funnel was resolved in preview/draft mode. */
  isPreview?: boolean;
  /** NX-12391: Config came from a snapshot because the session store is down. */
  sessionStoreUnavailable?: boolean;
};

const FunnelWithConfig = ({
  application,
  config,
  displayConfig,
  experimentId,
  armId,
  funnelVersion,
  inletId,
  isPreview,
  sessionStoreUnavailable,
}: FunnelWithConfigProps): ReactElement => {
  const anonymousId = useAnonymousId();
  const sessionState = useSessionInit({
    applicationId: application.id,
    anonymousId,
    config,
    experimentId,
    armId,
    funnelVersion,
    inletId,
    isPreview,
    ...(sessionStoreUnavailable ? { sessionStoreUnavailable: true } : {}),
  });

  if (sessionState.status === "loading") return <FunnelLoadingShell />;
  if (sessionState.status === "error") return <FunnelErrorShell message={sessionState.message} />;

  return (
    <FunnelEngineRunner
      application={application}
      config={config}
      displayConfig={displayConfig}
      sessionId={sessionState.sessionId}
      anonymousId={anonymousId}
      experimentId={experimentId}
      armId={armId}
      funnelVersion={funnelVersion}
      inletId={inletId}
      isPreview={isPreview}
    />
  );
};

const EMPTY_DISPLAY_CONFIG: Record<string, unknown> = {};

export const Funnel = (): ReactElement => {
  const applicationContext = useApplicationContext();

  // Fire Tapfiliate click creation as soon as the application context is ready.
  // Must be called unconditionally (React hook rules); internal guard handles
  // the "loading" and "no account ID" cases as no-ops.
  const application = applicationContext.status === "ready" ? applicationContext.application : null;
  useTapfiliateClick(application);

  if (applicationContext.status === "loading") return <FunnelLoadingShell />;
  if (applicationContext.status === "error") {
    return <FunnelErrorShell message={`application:${applicationContext.message}`} />;
  }
  if (!applicationContext.funnel) {
    return <FunnelErrorShell message="funnel_not_resolved" />;
  }
  return (
    <FunnelWithConfig
      application={applicationContext.application}
      config={applicationContext.funnel.config}
      displayConfig={applicationContext.funnel.display_config ?? EMPTY_DISPLAY_CONFIG}
      experimentId={applicationContext.funnel.experiment_id}
      armId={applicationContext.funnel.arm_id}
      funnelVersion={applicationContext.funnel.version}
      inletId={applicationContext.inlet?.inletId}
      isPreview={applicationContext.funnel.is_preview}
      sessionStoreUnavailable={applicationContext.degraded === true}
    />
  );
};

export default Funnel;
